import {
  Building2,
  ChevronDown,
  KanbanSquare,
  List as ListIcon,
  Plus,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ViewTabs } from "@/modules/shared/chrome";
import type { AccountsHubView } from "@/modules/accounts/accountsHubSearchParams";
import { cn } from "@/lib/utils";

export type AccountsListTab = "companies" | "people";

export type AccountsHubChrome = {
  activeView: AccountsHubView;
  onViewChange: (view: AccountsHubView) => void;
  /** List view: companies (default) or every person. */
  listTab?: AccountsListTab;
  onListTabChange?: (tab: AccountsListTab) => void;
  showToggle?: boolean;
  canCreateCompany?: boolean;
  canCreateContact?: boolean;
  onNewCompany?: () => void;
  onNewLead?: () => void;
  onNewContact?: () => void;
};

type AccountsModuleToolbarProps = AccountsHubChrome & {
  /** Search (list) or My leads (board) — sits between view toggle and create actions. */
  leadingExtra?: ReactNode;
  className?: string;
};

const AccountsCreateSplitButton = ({
  canCreateCompany = false,
  canCreateContact = false,
  onNewCompany,
  onNewLead,
  onNewContact,
}: Pick<
  AccountsHubChrome,
  | "canCreateCompany"
  | "canCreateContact"
  | "onNewCompany"
  | "onNewLead"
  | "onNewContact"
>) => {
  const showCompany = Boolean(canCreateCompany && onNewCompany);
  const showLead = Boolean(canCreateContact && onNewLead);
  const showContact = Boolean(canCreateContact && onNewContact);

  const primary = showCompany
    ? {
        label: "New account",
        onClick: onNewCompany!,
      }
    : showLead
      ? {
          label: "New lead",
          onClick: onNewLead!,
        }
      : showContact
        ? {
            label: "New contact",
            onClick: onNewContact!,
          }
        : null;

  if (!primary) return null;

  const menuItems = [
    showCompany && primary.label !== "New account"
      ? { label: "New account", onSelect: onNewCompany! }
      : null,
    showLead && primary.label !== "New lead"
      ? { label: "New lead", onSelect: onNewLead! }
      : null,
    showContact && primary.label !== "New contact"
      ? { label: "New contact", onSelect: onNewContact! }
      : null,
  ].filter(Boolean) as Array<{ label: string; onSelect: () => void }>;

  if (menuItems.length === 0) {
    return (
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={primary.onClick}
        aria-label={primary.label}
      >
        <Plus className="size-4" />
        {primary.label}
      </Button>
    );
  }

  return (
    <div className="flex shrink-0 items-stretch">
      <Button
        type="button"
        variant="primary"
        size="sm"
        className="rounded-r-none"
        onClick={primary.onClick}
        aria-label={primary.label}
      >
        <Plus className="size-4" />
        {primary.label}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="primary"
            size="sm"
            className="rounded-l-none border-l border-primary-foreground/20 px-2"
            aria-label="More create options"
          >
            <ChevronDown className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {menuItems.map((item) => (
            <DropdownMenuItem key={item.label} onSelect={item.onSelect}>
              <Plus className="size-4" />
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

/** Companies | People switch for the Accounts List. */
export const AccountsListTabToggle = ({
  value,
  onChange,
  className,
}: {
  value: AccountsListTab;
  onChange: (tab: AccountsListTab) => void;
  className?: string;
}) => (
  <ViewTabs
    value={value}
    onChange={onChange}
    tabs={[
      { value: "companies", label: "Companies", icon: Building2 },
      { value: "people", label: "People", icon: Users },
    ]}
    ariaLabel="Accounts"
    className={className}
  />
);

/**
 * In-page Accounts chrome: view toggle + module search/filters + create actions.
 * Keeps the global header for product-wide controls only.
 */
export const AccountsModuleToolbar = ({
  activeView,
  onViewChange,
  listTab = "companies",
  onListTabChange,
  showToggle = false,
  leadingExtra,
  canCreateCompany = false,
  canCreateContact = false,
  onNewCompany,
  onNewLead,
  onNewContact,
  className,
}: AccountsModuleToolbarProps) => (
  <div
    className={cn(
      "flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3",
      className,
    )}
  >
    {leadingExtra ? (
      <div className="min-w-0 flex-1">{leadingExtra}</div>
    ) : (
      <div className="min-w-0 flex-1" />
    )}

    <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
      {activeView === "list" && onListTabChange ? (
        <AccountsListTabToggle value={listTab} onChange={onListTabChange} />
      ) : null}
      {showToggle ? (
        <ViewTabs
          value={activeView}
          onChange={onViewChange}
          tabs={[
            { value: "list", label: "List", icon: ListIcon },
            { value: "board", label: "Board", icon: KanbanSquare },
          ]}
          ariaLabel="Accounts view"
        />
      ) : null}
      <AccountsCreateSplitButton
        canCreateCompany={canCreateCompany}
        canCreateContact={canCreateContact}
        onNewCompany={onNewCompany}
        onNewLead={onNewLead}
        onNewContact={onNewContact}
      />
    </div>
  </div>
);
